import { AimOutlined, CreditCardOutlined, PhoneOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Modal, Row, Space, Table, Typography } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";

import { ApiNames } from "../api/api";
import SupportIcon from "../assets/svg/SupportIcon";
import { LookbackPeriodUnits } from "../constant/assistanceProgramLookback";
import { BLUE_DARK, WHITE_GRAY } from "../constant/colors";
import ACTIONS from "../store/action";
import {
  selectIsFetching,
  selectSelectedAssistanceProgram,
  selectShowAssistanceProgramModal,
  selectShowSupport
} from "../store/selector";
import { parseFundsMaxToDisplay } from "../utils/parse";
import { ContactSupport } from "./ContactSupport";
import { ShorterTextWithTooltip } from "./ShorterTextWithTooltip";
import { formatPhoneNumber } from "../utils/formaters";

const { Text, Link } = Typography;

const BlueDarkText = styled(Text)`
  color: ${BLUE_DARK};
`;

const TabIndexMap = Object.freeze({
  0: "benefits",
  1: "eligibility",
  2: "clinical_information"
});

const applicationRenewalTimeTypes = Object.freeze({
  never: 1,
  everyXMonths: 2,
  endofCurrentFiscalYear: 3
});

const getEnrollExpiryDateText = (assistanceProgram, t) => {
  if (
    assistanceProgram.applicationRenewalTimeType === applicationRenewalTimeTypes.everyXMonths &&
    assistanceProgram.applicationRenewalTimeInMonths
  ) {
    return t("assistance_programs.modal.enrollment_expires_month", {
      applicationRenewalTimeInMonths: assistanceProgram.applicationRenewalTimeInMonths
    });
  }

  if (assistanceProgram.applicationRenewalTimeType === applicationRenewalTimeTypes.endofCurrentFiscalYear) {
    return t("assistance_programs.modal.enrollment_expires_end_of_year");
  }

  return "--";
};

const getLookbackPeriodText = (t, lookbackRelevanceTime, lookbackRelevanceUnits) => {
  if (lookbackRelevanceTime === 0) {
    return `${t("assistance_programs.modal.no_lookback_period")}`;
  }
  if (!lookbackRelevanceTime) {
    return `--`;
  }

  return `${t(
    lookbackRelevanceUnits === LookbackPeriodUnits.months
      ? "assistance_programs.modal.x_months_lookback"
      : "assistance_programs.modal.x_days_lookback",
    {
      count: lookbackRelevanceTime
    }
  )}`;
};

function AssistanceProgramModalComponent({
  t,
  visible,
  closeModal,
  assistanceProgram,
  isFetching,
  setShowSupport,
  showSupport
}) {
  const [modalTab, setModalTab] = useState(TabIndexMap[0]);

  useEffect(() => {
    setModalTab(TabIndexMap[0]);
  }, [visible]);

  const tabList = [
    {
      key: TabIndexMap[0],
      tab: t("assistance_programs.modal.benefits")
    },
    {
      key: TabIndexMap[1],
      tab: t("assistance_programs.modal.eligibility")
    },
    {
      key: TabIndexMap[2],
      tab: t("assistance_programs.modal.clinical_information")
    }
  ];

  const {
    requiredCommercialInsurance,
    requiredMedicaidInsurance,
    requiredMedicareInsurance,
    requiredFederalInsurance,
    requiredUninsuredInsurance,
    requiredUnderinsuredInsurance,
    phone1,
    phone2,
    fax
  } = assistanceProgram || {};

  const requiredCoverageTypes = [
    { title: "Commercial", value: requiredCommercialInsurance },
    { title: "Medicare", value: requiredMedicareInsurance },
    { title: "Medicaid", value: requiredMedicaidInsurance },
    { title: "Federal", value: requiredFederalInsurance },
    { title: "Uninsured", value: requiredUninsuredInsurance },
    { title: "Underinsured", value: requiredUnderinsuredInsurance }
  ].filter(({ value }) => value);

  return (
    <Modal
      centered
      open={visible}
      width={"75%"}
      bodyStyle={{ minHeight: 750, padding: "24px 24px 0px 24px" }}
      onCancel={() => {
        setShowSupport(false, undefined);
        closeModal();
      }}
      footer={null}
      destroyOnClose={true}
    >
      {showSupport.visible && <ContactSupport />}
      {!showSupport.visible && (
        <Row>
          <Col lg={16} xl={18}>
            <Card
              loading={isFetching}
              bordered={false}
              title={
                assistanceProgram && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Space direction={"vertical"} style={{ padding: 0 }}>
                      <ShorterTextWithTooltip text={assistanceProgram.name} maxLength={100} style={{ fontSize: 18 }} />
                      <Text style={{ fontSize: 16 }}>{assistanceProgram.providedBy}</Text>
                    </Space>
                    <img
                      src={assistanceProgram.logo}
                      alt=""
                      width="170"
                      height="60"
                      style={{ padding: "6px 20px 0px 0px" }}
                    />
                  </div>
                )
              }
              onTabChange={(key) => setModalTab(key)}
              tabList={tabList}
              tabProps={{ id: "pap" }}
              style={{ padding: 0 }}
              headStyle={{ padding: 0 }}
              bodyStyle={{ padding: "24px 0px 24px 0px" }}
            >
              {assistanceProgram && modalTab === tabList[0].key && (
                <Space size={10} direction={"vertical"}>
                  <Text style={{ whiteSpace: "pre-line" }}>{assistanceProgram.description}</Text>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      LOOKBACK PERIOD
                    </Text>
                    <Text>
                      {getLookbackPeriodText(
                        t,
                        assistanceProgram?.lookbackRelevanceTime,
                        assistanceProgram?.lookbackRelevanceUnits
                      )}
                    </Text>
                  </Space>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      {t("assistance_programs.modal.enrollment_info")}
                    </Text>
                    {!assistanceProgram?.acceptedEnrolls?.length &&
                    assistanceProgram.patientConsentRequired === null ? (
                      <Text>--</Text>
                    ) : (
                      <>
                        <Text>
                          {`${t("assistance_programs.modal.who_can_enroll")}: `}
                          {(assistanceProgram?.acceptedEnrolls?.length &&
                            assistanceProgram.acceptedEnrolls.join(", ")) ||
                            "--"}
                        </Text>
                        <Text>
                          {`${t("assistance_programs.modal.patient_authorization")}: `}
                          {assistanceProgram.patientConsentRequired == null
                            ? "--"
                            : assistanceProgram.patientConsentRequired
                            ? t("assistance_programs.modal.patient_consent_required")
                            : t("assistance_programs.modal.patient_consent_not_required")}
                        </Text>
                      </>
                    )}
                  </Space>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      MAXIMUM NUMBER OF USES
                    </Text>
                    <Text>{assistanceProgram.maxNumberOfUses ? assistanceProgram.maxNumberOfUses : "--"}</Text>
                  </Space>
                  {assistanceProgram.extendedDetails && (
                    <Space size={2} direction={"vertical"}>
                      <Text strong style={{ color: BLUE_DARK }}>
                        PROGRAM DETAILS
                      </Text>
                      <Table
                        showHeader={false}
                        pagination={false}
                        columns={[{ dataIndex: "name", key: "name" }]}
                        dataSource={assistanceProgram.extendedDetails.map((data, index) => ({
                          ...data,
                          key: index
                        }))}
                        scroll={{ y: 250 }}
                      />
                    </Space>
                  )}
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      ENROLLMENT EXPIRATION DATE
                    </Text>
                    <Text>{getEnrollExpiryDateText(assistanceProgram, t)}</Text>
                  </Space>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      OFFER EXPIRATION DATE
                    </Text>
                    <Text>
                      {assistanceProgram.offerExpiration
                        ? moment(assistanceProgram.offerExpiration).format("MM-DD-YYYY")
                        : "--"}
                    </Text>
                  </Space>
                </Space>
              )}
              {assistanceProgram && modalTab === tabList[1].key && (
                <Space size={10} direction={"vertical"}>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      INSURANCE CRITERIA
                    </Text>
                    {requiredCoverageTypes.length ? requiredCoverageTypes.map(({ title }) => title).join(" / ") : "--"}
                  </Space>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      INCOME REQUIRED (FPL)
                    </Text>
                    <Text>{assistanceProgram.maxFpl > 0 ? `${assistanceProgram.maxFpl}%` : "--"}</Text>
                  </Space>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      ELIGIBILITY CRITERIA
                    </Text>
                    {assistanceProgram.eligibilityRequirements && assistanceProgram.eligibilityRequirements.length ? (
                      <Table
                        showHeader={false}
                        pagination={false}
                        columns={[{ dataIndex: "name", key: "name" }]}
                        dataSource={assistanceProgram.eligibilityRequirements.map((data, index) => ({
                          ...data,
                          key: index
                        }))}
                        scroll={{ y: 250 }}
                      />
                    ) : (
                      "--"
                    )}
                  </Space>
                </Space>
              )}
              {assistanceProgram && modalTab === tabList[2].key && (
                <Space size={10} direction={"vertical"}>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      DRUG(S)
                    </Text>
                    {assistanceProgram.possibleDrugs ? (
                      <Text>
                        {assistanceProgram.possibleDrugs.map(({ apDrugDisplayName }) => apDrugDisplayName).join(", ")}
                      </Text>
                    ) : (
                      "---"
                    )}
                  </Space>
                  <Space size={2} direction={"vertical"}>
                    <Text strong style={{ color: BLUE_DARK }}>
                      CONDITION(S)
                    </Text>
                    <Table
                      showHeader={false}
                      pagination={false}
                      columns={[{ dataIndex: "name", key: "name" }]}
                      dataSource={assistanceProgram.requiredDiagnosis.split(";").map((data, index) => ({
                        name: data,
                        key: index
                      }))}
                      scroll={{ y: 250 }}
                    />
                  </Space>
                </Space>
              )}
            </Card>
          </Col>
          <Col lg={8} xl={6} md={24} style={{ paddingTop: 16, borderLeft: `1px solid ${WHITE_GRAY}` }}>
            {assistanceProgram && (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#54cda8",
                    justifyContent: "center",
                    paddingLeft: 16,
                    marginBottom: 16,
                    minHeight: 100
                  }}
                >
                  <Text strong style={{ color: "white", fontSize: 20 }}>
                    {parseFundsMaxToDisplay(assistanceProgram)}
                  </Text>
                  <Text style={{ color: "white", fontSize: 16 }}>Potential Savings</Text>
                </div>
                <Space size={12} direction={"vertical"} style={{ paddingLeft: 16, width: "100%" }}>
                  {assistanceProgram?.url && (
                    <Space>
                      <CreditCardOutlined style={{ color: BLUE_DARK }} />
                      <Link id="pap_application_link" href={assistanceProgram.url} target="_blank">
                        {t("assistance_programs.modal.provider_portal")}
                      </Link>
                    </Space>
                  )}
                  {assistanceProgram?.pharmacyUrl && (
                    <Space>
                      <CreditCardOutlined style={{ color: BLUE_DARK }} />
                      <Link id="pap_pharmacy_url" href={assistanceProgram.pharmacyUrl} target="_blank">
                        {t("assistance_programs.modal.pharmacy_portal")}
                      </Link>
                    </Space>
                  )}
                  {assistanceProgram?.website && (
                    <Space>
                      <CreditCardOutlined style={{ color: BLUE_DARK }} />
                      <Link id="pap_program_website" href={assistanceProgram.website} target="_blank">
                        {t("assistance_programs.modal.program_website")}
                      </Link>
                    </Space>
                  )}
                  {assistanceProgram?.pharmacyWebsite && (
                    <Space>
                      <CreditCardOutlined style={{ color: BLUE_DARK }} />
                      <Link pap="pap_pharmacy_website" href={assistanceProgram.pharmacyWebsite} target="_blank">
                        {t("assistance_programs.modal.pharmacy_website")}
                      </Link>
                    </Space>
                  )}

                  {(phone1 || phone2 || fax) && (
                    <Space>
                      <PhoneOutlined style={{ color: BLUE_DARK }} />
                      <Space style={{ whiteSpace: "nowrap", flexWrap: "wrap" }} size={0}>
                        {phone1 && (
                          <div>
                            <BlueDarkText>{t("assistance_programs.modal.phone1")}: </BlueDarkText>
                            <Text>{formatPhoneNumber(phone1)}</Text>
                            {(phone2 || fax) && <span>,&nbsp;</span>}
                          </div>
                        )}
                        {phone2 && (
                          <div>
                            <BlueDarkText>{t("assistance_programs.modal.phone2")}: </BlueDarkText>
                            <Text>{formatPhoneNumber(phone2)}</Text>
                            {fax && <span>,&nbsp;</span>}
                          </div>
                        )}
                        {fax && (
                          <div>
                            <BlueDarkText>{t("assistance_programs.modal.fax")}: </BlueDarkText>
                            <Text>{formatPhoneNumber(fax)}</Text>
                          </div>
                        )}
                      </Space>
                    </Space>
                  )}

                  <Space>
                    <AimOutlined style={{ color: BLUE_DARK }} />
                    <Text style={{ color: BLUE_DARK }}>{assistanceProgram.address}</Text>
                  </Space>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <Text style={{ color: BLUE_DARK }}>Helpdesk Name</Text>
                    <ShorterTextWithTooltip
                      style={{
                        color: BLUE_DARK,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textAlign: "left"
                      }}
                      maxLength={12}
                      text={assistanceProgram.helpdeskName?.trim().length > 0 ? assistanceProgram.helpdeskName : "---"}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <Text style={{ color: BLUE_DARK }}>Helpdesk Phone</Text>
                    <ShorterTextWithTooltip
                      style={{
                        color: BLUE_DARK,
                        maxWidth: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textAlign: "left"
                      }}
                      maxLength={12}
                      text={
                        assistanceProgram.helpdeskPhone?.trim().length > 0
                          ? formatPhoneNumber(assistanceProgram.helpdeskPhone)
                          : "---"
                      }
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text style={{ color: BLUE_DARK }}>BIN</Text>
                    <Text style={{ color: BLUE_DARK }}>{assistanceProgram.bin ? assistanceProgram.bin : "---"}</Text>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text style={{ color: BLUE_DARK }}>PCN</Text>
                    <Text style={{ color: BLUE_DARK }}>{assistanceProgram.pcn ? assistanceProgram.pcn : "---"}</Text>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text style={{ color: BLUE_DARK }}>Group Number</Text>
                    <Text style={{ color: BLUE_DARK }}>
                      {assistanceProgram.groupNumber ? assistanceProgram.groupNumber : "---"}
                    </Text>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Divider />
                    <Button
                      id="pap_contact_support"
                      type="text"
                      onClick={() => setShowSupport(true, assistanceProgram)}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <SupportIcon />
                        <Text style={{ paddingLeft: 4, color: "#2B56A3" }}>{t("contact_support")}</Text>
                      </div>
                    </Button>
                  </div>
                </Space>
              </>
            )}
          </Col>
        </Row>
      )}
    </Modal>
  );
}

const mapDispatchToProps = (dispatch) => ({
  closeModal: () => dispatch(ACTIONS.setShowAssistanceProgramModal(false)),
  setShowSupport: (visible, assistanceProgram) => dispatch(ACTIONS.setShowSupport(visible, assistanceProgram))
});

const mapStateToProps = createStructuredSelector({
  assistanceProgram: selectSelectedAssistanceProgram,
  visible: selectShowAssistanceProgramModal,
  showSupport: selectShowSupport,
  isFetching: selectIsFetching([ApiNames.AssistanceProgram])
});

const AssistanceProgramModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(withTranslation()(AssistanceProgramModalComponent)));

export { AssistanceProgramModal };
